/**
 * Symbol ID Mapper + Grid Converter
 *
 * Maps between backend numeric symbol IDs and local string IDs.
 * Converts API column-based grids to local row-based GridCell arrays.
 */

import type { BackendSymbolDef } from '@core/types/backend-game.types';
import type { CascadeSlotConfig, GridCell, WinResult } from '@core/types/cascade-slot.types';

export interface SymbolIdMap {
  numericToString: Map<number, string>;  // 1 → 'H1'
  stringToNumeric: Map<string, number>;  // 'H1' → 1
}

/**
 * Build a bidirectional map from backend symbol definitions.
 */
export function buildSymbolIdMap(backendSymbols: BackendSymbolDef[]): SymbolIdMap {
  const numericToString = new Map<number, string>();
  const stringToNumeric = new Map<string, number>();

  for (const sym of backendSymbols) {
    numericToString.set(sym.id, sym.code);
    stringToNumeric.set(sym.code, sym.id);
  }

  return { numericToString, stringToNumeric };
}

/**
 * Convert an API column-based grid to a local row-based GridCell[][].
 *
 * API format: apiGrid[columnIndex][rowIndex] (outer = columns, inner = rows)
 * Local format: grid[rowIndex][columnIndex] (outer = rows, inner = columns)
 */
export function convertApiGridToLocal(
  apiGrid: number[][],
  map: SymbolIdMap,
  config: CascadeSlotConfig
): GridCell[][] {
  if (!apiGrid || apiGrid.length === 0) return [];

  const columns = apiGrid.length;
  const rows = config.rows;
  const reelHeights = config.reelHeights;

  // Initialize grid as rows x columns
  const grid: GridCell[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: GridCell[] = [];
    for (let c = 0; c < columns; c++) {
      // Check variable reel heights: out-of-bounds cells get empty symbolId
      const colHeight = reelHeights?.[c] ?? rows;
      const startRow = rows - colHeight;

      if (r < startRow) {
        row.push({
          symbolId: '',
          multiplierValue: 0,
          isWinning: false,
          isRemoving: false,
          isNew: false,
          isFalling: false,
          fallDistance: 0,
          columnIndex: c,
        });
        continue;
      }

      // Transpose: apiGrid[col][localRowInCol] where localRowInCol = r - startRow
      const localRowInCol = r - startRow;
      const numericId = apiGrid[c]?.[localRowInCol];

      if (numericId === undefined || numericId === null) {
        row.push({
          symbolId: '',
          multiplierValue: 0,
          isWinning: false,
          isRemoving: false,
          isNew: false,
          isFalling: false,
          fallDistance: 0,
          columnIndex: c,
        });
        continue;
      }

      const symbolId = map.numericToString.get(numericId) ?? '';

      // For BN (bonus) symbols: assign random multiplier from pool
      let multiplierValue = 0;
      if (symbolId === 'BN' && config.multiplierPool.length > 0) {
        const totalWeight = config.multiplierPool.reduce((sum, e) => sum + e.weight, 0);
        let rand = Math.random() * totalWeight;
        for (const entry of config.multiplierPool) {
          rand -= entry.weight;
          if (rand <= 0) {
            multiplierValue = entry.value;
            break;
          }
        }
        if (multiplierValue === 0) {
          multiplierValue = config.multiplierPool[0].value;
        }
      }

      row.push({
        symbolId,
        multiplierValue,
        isWinning: false,
        isRemoving: false,
        isNew: true,
        isFalling: false,
        fallDistance: 0,
        columnIndex: c,
      });
    }
    grid.push(row);
  }

  return grid;
}

/**
 * Convert API winning positions to local WinResult[].
 *
 * API format: ws[groupIndex][colIndex] = [rowIndex, rowIndex, ...]
 * Each entry in the group corresponds to a participating column (sequential from col 0),
 * and contains an array of row indices where the winning symbol appears in that column.
 *
 * Example: ws = [[[2],[1],[1],[2]]] means one win group:
 *   col 0 row 2, col 1 row 1, col 2 row 1, col 3 row 2
 */
export function convertApiWinsToLocal(
  winningPositions: number[][][],
  winReward: number[],
  map: SymbolIdMap,
  grid: GridCell[][]
): WinResult[] {
  if (!winningPositions || winningPositions.length === 0) return [];

  const wins: WinResult[] = [];

  for (let i = 0; i < winningPositions.length; i++) {
    const group = winningPositions[i];
    if (!group || group.length === 0) continue;

    const positions: { row: number; col: number }[] = [];

    // Detect flat vs nested format:
    // Flat: group = [4, 7, 12, 14, 15] — flat position indices (row-major)
    // Nested: group = [[2],[1],[1],[2]] — group[colIdx] = [rowIdx, ...]
    const isFlat = group.length > 0 && typeof group[0] === 'number';

    if (isFlat) {
      // Flat position indices: convert to {row, col} using row-major order
      const numCols = grid[0]?.length ?? 0;
      for (const flatIdx of group as unknown as number[]) {
        if (typeof flatIdx === 'number' && numCols > 0) {
          const row = Math.floor(flatIdx / numCols);
          const col = flatIdx % numCols;
          if (row >= 0 && row < grid.length && col >= 0 && col < numCols) {
            positions.push({ row, col });
          }
        }
      }
    } else {
      // Nested format: group[colIdx] = [rowIdx, ...] — each entry is row indices for that column
      for (let colIdx = 0; colIdx < group.length; colIdx++) {
        const rowIndices = group[colIdx];
        if (!rowIndices || !Array.isArray(rowIndices)) continue;
        for (const rowIdx of rowIndices) {
          if (typeof rowIdx === 'number') {
            positions.push({ row: rowIdx, col: colIdx });
          }
        }
      }
    }

    if (positions.length === 0) continue;

    // Get symbolId from the first position in the grid
    const firstPos = positions[0];
    const symbolId = (grid[firstPos.row]?.[firstPos.col]?.symbolId) ?? '';

    wins.push({
      symbolId,
      count: positions.length,
      positions,
      payout: winReward[i] ?? 0,
      bracketIndex: 0,
    });
  }

  return wins;
}

/**
 * Apply fall animation flags by comparing old grid (post-removal) vs new grid (from API).
 *
 * - Cells that were empty and now have a symbol → isNew=true
 * - Cells where the symbol changed position (fell down) → isFalling=true, fallDistance=delta
 * - Unchanged cells → no animation flags
 */
export function applyFallAnimationFlags(
  oldGrid: GridCell[][],
  newGrid: GridCell[][]
): GridCell[][] {
  if (newGrid.length === 0) return newGrid;

  const rows = newGrid.length;
  const cols = newGrid[0]?.length ?? 0;

  const result: GridCell[][] = [];

  for (let r = 0; r < rows; r++) {
    const row: GridCell[] = [];
    for (let c = 0; c < cols; c++) {
      const newCell = newGrid[r][c];
      const oldCell = oldGrid[r]?.[c];

      if (!newCell || !newCell.symbolId) {
        row.push({ ...(newCell || { symbolId: '', multiplierValue: 0, isWinning: false, isRemoving: false, isNew: false, isFalling: false, fallDistance: 0, columnIndex: c }) });
        continue;
      }

      const wasEmpty = !oldCell || !oldCell.symbolId;

      if (wasEmpty) {
        // New cell appeared (either fell from above or newly generated)
        // Look upward in old grid to find where this symbol came from
        let fallDist = 0;
        for (let scanRow = r - 1; scanRow >= 0; scanRow--) {
          const scanCell = oldGrid[scanRow]?.[c];
          if (scanCell && scanCell.symbolId === newCell.symbolId) {
            fallDist = r - scanRow;
            break;
          }
        }

        if (fallDist > 0) {
          row.push({
            ...newCell,
            isFalling: true,
            fallDistance: fallDist,
            isNew: false,
            columnIndex: c,
          });
        } else {
          // Truly new (generated from top)
          row.push({
            ...newCell,
            isNew: true,
            isFalling: false,
            fallDistance: 0,
            columnIndex: c,
          });
        }
      } else if (oldCell.symbolId === newCell.symbolId) {
        // Same symbol, no movement
        row.push({
          ...newCell,
          isNew: false,
          isFalling: false,
          fallDistance: 0,
          isWinning: false,
          isRemoving: false,
          columnIndex: c,
        });
      } else {
        // Different symbol - treat as new (API replaced it)
        row.push({
          ...newCell,
          isNew: true,
          isFalling: false,
          fallDistance: 0,
          columnIndex: c,
        });
      }
    }
    result.push(row);
  }

  return result;
}
